import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { api } from '../api/client'
import { keys, useLabels } from '../api/hooks'
import { labelTree } from '../lib/cache'
import { LABEL_COLORS } from '../lib/labelColors'
import { useUi } from '../store/ui'

function ConfirmDialog() {
  const req = useUi((s) => s.confirmReq)
  const setConfirm = useUi((s) => s.setConfirm)
  const done = (ok: boolean) => { req?.resolve(ok); setConfirm(null) }
  return (
    <Dialog open={Boolean(req)} onClose={() => done(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{req?.title}</DialogTitle>
      {req?.body && <DialogContent><DialogContentText>{req.body}</DialogContentText></DialogContent>}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => done(false)}>Cancel</Button>
        <Button variant="contained" color={req?.danger ? 'error' : 'primary'} onClick={() => done(true)} autoFocus>{req?.confirmLabel ?? 'OK'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function AskDialog() {
  const req = useUi((s) => s.askReq)
  const setAsk = useUi((s) => s.setAsk)
  const [value, setValue] = useState('')
  useEffect(() => { if (req) setValue(req.initial ?? '') }, [req])
  const error = req?.validate && value ? req.validate(value) : null
  const done = (v: string | null) => { req?.resolve(v); setAsk(null) }
  return (
    <Dialog open={Boolean(req)} onClose={() => done(null)} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); if (value.trim() && !error) done(value.trim()) }}>
        <DialogTitle>{req?.title}</DialogTitle>
        <DialogContent><TextField autoFocus fullWidth margin="dense" label={req?.label} value={value} onChange={(e) => setValue(e.target.value)} error={Boolean(error)} helperText={error} /></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => done(null)}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!value.trim() || Boolean(error)}>{req?.confirmLabel ?? 'OK'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const labelSchema = z.object({ name: z.string().trim().min(1, 'Enter a label name').max(60).refine((s) => !s.includes('/'), 'A label name cannot contain "/"'), parentId: z.string().nullable(), color: z.string() })
type LabelForm = z.infer<typeof labelSchema>

/** Create / edit a label: name, optional parent (nesting) and one of the palette colors. */
function LabelDialog() {
  const state = useUi((s) => s.labelDialog)
  const setLabelDialog = useUi((s) => s.setLabelDialog)
  const notify = useUi((s) => s.notify)
  const labels = useLabels()
  const qc = useQueryClient()
  const editing = state?.label
  const { register, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm<LabelForm>({ resolver: zodResolver(labelSchema), defaultValues: { name: '', parentId: null, color: LABEL_COLORS[5]! } })
  useEffect(() => { if (state) reset({ name: editing?.name ?? '', parentId: editing ? editing.parentId : state.parentId ?? null, color: editing?.color ?? LABEL_COLORS[5]! }) }, [state, editing, reset])
  const color = watch('color')
  const options = labelTree(labels.data ?? []).filter((l) => l.id !== editing?.id)
  const close = () => setLabelDialog(null)
  const submit = handleSubmit(async (v) => {
    try {
      if (editing) await api(`/labels/${editing.id}`, { method: 'PATCH', body: v })
      else await api('/labels', { method: 'POST', body: v })
      await qc.invalidateQueries({ queryKey: keys.labels })
      close()
    } catch (e) { notify({ message: e instanceof Error ? e.message : 'Could not save label', severity: 'error' }) }
  })
  return (
    <Dialog open={Boolean(state)} onClose={close} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle>{editing ? 'Edit label' : 'New label'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
          <TextField autoFocus label="Label name" error={!!errors.name} helperText={errors.name?.message} {...register('name')} />
          <TextField select label="Nest label under" value={watch('parentId') ?? ''} onChange={(e) => setValue('parentId', e.target.value || null)}>
            <MenuItem value="">None (top level)</MenuItem>
            {options.map((l) => <MenuItem key={l.id} value={l.id} sx={{ pl: 2 + l.depth * 2 }}>{l.name}</MenuItem>)}
          </TextField>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
            <Box role="radiogroup" aria-label="Label color" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {LABEL_COLORS.map((c) => (
                <Box key={c} component="button" type="button" role="radio" aria-checked={c === color} aria-label={`Color ${c}`} onClick={() => setValue('color', c)}
                  sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, p: 0, cursor: 'pointer', border: '3px solid', borderColor: c === color ? 'text.primary' : 'transparent', outlineOffset: 2 }} />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>Save</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export function DialogHost() {
  return (
    <>
      <ConfirmDialog />
      <AskDialog />
      <LabelDialog />
    </>
  )
}
